import React, { useEffect, useState } from "react";
import { Doughnut, Pie } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels"; // Import the plugin

// Register Chart.js components for doughnut/pie charts
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels); // Register the plugin

// Function to generate random RGBA color
function getRandomColor(opacity = 0.8) {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export default function Weaknesses({
  data,
  type = "doughnut",
  title = "",
  datalabels = false,
}) {
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [validatedData, setValidatedData] = useState([]); // Store validated data for tooltip
  const [selectedLegend, setSelectedLegend] = useState(null); // Track selected legend item

  useEffect(() => {
    // Extract fields directly from data
    const labels = data.map((item) => item.Title); // Use "Title" for labels
    const counts = data.map((item) => item.Count); // Use "Count" for data

    // Generate a color map based on legend values
    const legendColorMap = {};
    data.forEach((item) => {
      if (!legendColorMap[item.Title]) {
        legendColorMap[item.Title] = getRandomColor();
      }
    });

    // Prepare the chart data
    const formattedChartData = {
      labels, // Labels are the titles
      datasets: [
        {
          label: "Count", // Dataset label
          data: counts, // Data values
          borderWidth: 1,
          borderColor: "#fff",
          backgroundColor: labels.map((label) => legendColorMap[label]), // Use generated colors
        },
      ],
    };

    setValidatedData(data); // Store original data for tooltip access
    setChartData(formattedChartData);
    setIsLoading(false);
  }, [data]);

  // Function to update chart data based on selected legend
  const updateChartData = (selectedItem) => {
    if (!selectedItem) {
      // Reset to show all data
      const labels = validatedData.map((item) => item.Title);
      const counts = validatedData.map((item) => item.Count);
      const backgroundColors = labels.map((label) => getRandomColor());
      setChartData({
        labels,
        datasets: [
          {
            ...chartData.datasets[0],
            data: counts,
            backgroundColor: backgroundColors,
          },
        ],
      });
      setSelectedLegend(null);
    } else {
      // Filter data to show only the selected legend item
      const filteredData = validatedData.filter(
        (item) => item.Title === selectedItem.text
      );
      const labels = filteredData.map((item) => item.Title);
      const counts = filteredData.map((item) => item.Count);
      const backgroundColors = labels.map(() => selectedItem.fillStyle); // Use the selected item's color
      setChartData({
        labels,
        datasets: [
          {
            ...chartData.datasets[0],
            data: counts,
            backgroundColor: backgroundColors,
          },
        ],
      });
      setSelectedLegend(selectedItem.text);
    }
  };

  // Chart options
  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom", // Move legend to the bottom
        align: "start", // Align legend items to the left
        labels: {
          textAlign: "left", // Align text inside legend items to the left
          boxWidth: 12,
          boxHeight: 12,
          padding: 10, // Add padding between legend items
          font: {
            size: 12,
            family: "Arial",
          },
          color: "#333",
          generateLabels: (chart) => {
            const { data } = chart;
            return data.labels.map((label, index) => {
              const isSelected = selectedLegend === label;
              return {
                text: label, // Use the label (Title) as the legend text
                fillStyle: data.datasets[0].backgroundColor[index], // Use the dataset's color
                strokeStyle: isSelected ? "#000" : "#fff", // Highlight selected legend
                lineWidth: isSelected ? 2 : 1, // Bold border for selected legend
                fontColor: isSelected ? "#000" : "#333", // Darker text for selected legend
                hidden: selectedLegend && selectedLegend !== label, // Hide unselected
              };
            });
          },
        },
        onClick: (e, legendItem, legend) => {
          if (selectedLegend === legendItem.text) {
            updateChartData(null);
          } else {
            updateChartData(legendItem);
          }
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const item = validatedData[index];
            return `${item.Title}: ${item.Count}`;
          },
        },
      },
      datalabels: {
        display: datalabels, // Enable data labels
        formatter: (value) => {
          return value; // Display only the Count
        },
        color: "#fff", // Label text color (white for contrast)
        font: {
          weight: "bold", // Bold font for better visibility
          size: 12, // Font size
        },
        anchor: "center", // Center the label within the segment
        align: "center", // Align the label in the center
      },
    },
    layout: {
      padding: {
        bottom: 20, // Add padding at the bottom to accommodate the legend
      },
    },
  };

  // Fixed height for simplicity (adjust as needed)
  const chartHeight = 500;

  // Select chart component based on type prop
  const ChartComponent = type.toLowerCase() === "pie" ? Pie : Doughnut;

  return (
    <div style={{ height: `${chartHeight}px`, width: "100%" }}>
      {isLoading ? (
        <p>Loading chart...</p>
      ) : chartData ? (
        <ChartComponent data={chartData} options={options} />
      ) : (
        <p>No chart data available.</p>
      )}
    </div>
  );
}
